/**
 * Bonus Engine v2 Admin Panel
 * Main view integrating all bonus management components
 */

import { ChangeEvent, useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { TopBar } from "components/navigation/TopBar"

import { BONUS_TABS, BonusTabType, TAB_CONTENT } from "features/bonus2/helpers/bonus2.helpers"

import { useProps } from "contexts/PropsContext"

import { BONUS2_TAB_PARAM } from "constants/api/requests"
import { tenantOptions } from "constants/bonus"

export const Bonus2Admin = () => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { user } = useProps()

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get(BONUS2_TAB_PARAM)
  const currentTab: BonusTabType = BONUS_TABS.find((tab) => tab.id === tabParam)?.id ?? "bonuses"
  const [bonusTenant, setBonusTenant] = useState<string>("")

  const setCurrentTab = useCallback(
    (next: BonusTabType) => {
      const params = new URLSearchParams(searchParams)
      params.set(BONUS2_TAB_PARAM, next)
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  useEffect(() => {
    if (!bonusTenant && tenantOptions.length > 0) {
      setBonusTenant(tenantOptions[0].value)
    }
  }, [])

  const handleTenantChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    setBonusTenant(ev.target.value)
  }

  const TabComponent = TAB_CONTENT[currentTab]

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("bonus2.page.title")} />
      {user && <WelcomeSection />}

      <div className="flex items-center gap-4">
        <label className="text-white font-medium">{t("bonus2.page.tenant")}</label>
        <DropdownMenu
          placeholder={t("bonus2.page.selectTenant")}
          options={tenantOptions}
          value={bonusTenant}
          onChange={handleTenantChange}
          rounded
        />
      </div>

      <div className="tabs gap-2">
        {BONUS_TABS.map((tab) => (
          <a
            key={tab.id}
            onClick={() => setCurrentTab(tab.id)}
            className={clsx(
              "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
              {
                "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": currentTab === tab.id,
                "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)] hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                  currentTab !== tab.id
              }
            )}
          >
            {t(tab.label)}
          </a>
        ))}
      </div>

      {bonusTenant ? (
        <TabComponent tenant={bonusTenant} />
      ) : (
        <div className="text-center text-gray-400 py-12">
          {t("bonus2.page.selectTenantPrompt", { section: currentTab.replace("-", " ") })}
        </div>
      )}
    </div>
  )
}
